/**
 * EIN KNOPF „ANSICHT ▾" STATT FUENF UMSCHALTERN (Rafael, 03.10.2026 10:00: Ja zu
 * „Die fünf Umschalter oben … zu einem Knopf ‚Ansicht ▾' mit beschriftetem Menü
 * zusammenfassen; der Guide bleibt eigener Knopf." — Begruendung 09:37: „das Umschalten
 * zwischen den Modi ist für einen normalen Nutzer nicht nachvollziehbar").
 *
 * Vorher standen oben verstreut: Dokument·Tabs·Bühne (an der Buehne), „Ansicht · Auto",
 * „Layout (n)", ◂Guide/▸Kanzler/⛶Fokus, die vier Anordnungssymbole und B1/B2. Jede Gruppe
 * steht jetzt mit Ueberschrift und einem Satz in einfachem Deutsch in diesem Menue.
 * Nichts ist weggefallen; die data-ai-id der alten Knoepfe tragen die Menuepunkte weiter.
 * Was gerade gilt, steht am Knopf selbst („Ansicht: Reiter ▾").
 */
import type { ReactNode } from 'react';
import KopfMenue from './KopfMenue';
import DisplayScaleControl from './DisplayScaleControl';
import { ABWEICHUNG_KURZ, ANORDNUNG_MODI, Bild as AnordnungBild, TITEL as ANORDNUNG_TITEL } from './GuideAnordnungSchalter';
import { kanzlerEingeklappt, setUiPreferences, useUiPreferences } from '../lib/useUiPreferences';
import { BUEHNEN_MODUS_TEXT, useBuehnenAnsicht } from '../lib/buehnenAnsicht';
import { guideAnordnungSicht, type GuideAnordnungModus } from '../../shared/guide-anordnung';
import type { BuehnenModus } from '../../shared/buehnen-anordnung';

const DARSTELLUNG = { auto: 'Auto', standard: 'Standard', compact: 'Kompakt' } as const;

function Gruppe({ titel, satz, aiId, children }: { titel: string; satz: string; aiId: string; children: ReactNode }) {
  return <section className="cui-ansicht-gruppe" role="group" aria-label={titel} data-ai-id={aiId}>
    <div className="cui-ansicht-gruppe-titel">{titel}</div>
    <p>{satz}</p>
    {children}
  </section>;
}

function Wahl({ an, aiId, onClick, disabled, title, children }: {
  an: boolean; aiId: string; onClick: () => void; disabled?: boolean; title?: string; children: ReactNode;
}) {
  return <button type="button" className="cui-ansicht-wahl" aria-pressed={an} data-ai-id={aiId}
    disabled={disabled} title={title} onClick={onClick}>{children}</button>;
}

export default function AnsichtMenue(props: {
  guideActive: boolean;
  /** Ist die Guide-Spalte gerade eingeklappt? Nur bei offenem Guide gesetzt. */
  guideEingeklappt?: boolean;
  anordnungModus?: GuideAnordnungModus;
  anordnungFestgelegt: boolean;
  onAnordnung?: (m: GuideAnordnungModus) => void;
  screenId: string;
  onScreenChange?: (id: string) => void;
  /** „Offene Chats anordnen" (frueher „Layout (n)"). */
  fehlendeChats: number;
  layoutLaeuft: boolean;
  layoutMeldung: string;
  layoutFarbe?: string;
  onLayout: () => void;
}) {
  const ui = useUiPreferences();
  const teilSicht = props.anordnungModus ? guideAnordnungSicht(props.anordnungModus) : { guide: true, chat: true };
  const guideTeilWegAnordnung = !teilSicht.guide;
  const kanzlerTeilWegAnordnung = !teilSicht.chat;
  const guideTeilAn = !props.guideEingeklappt && !guideTeilWegAnordnung;
  /* Dieselbe Regel wie die Spalte (App.tsx, chatSichtbar). */
  const kanzlerZu = kanzlerEingeklappt(ui, props.anordnungModus);
  const kanzlerTeilAn = !props.guideEingeklappt && !kanzlerTeilWegAnordnung && !kanzlerZu;
  const buehne = useBuehnenAnsicht();
  const { guideActive, anordnungModus, anordnungFestgelegt, onAnordnung, screenId, onScreenChange, fehlendeChats } = props;

  const stand = buehne ? BUEHNEN_MODUS_TEXT[buehne.modus].kurz : DARSTELLUNG[ui.mode];
  // Steht der Kanzler NICHT an der Standardstelle (unter dem Guide), zeigt es der Knopf —
  // sonst merkt man eine versehentliche Umstellung erst am Bild (Rafael, 03.10.2026 10:29).
  const kanzlerAbweichung = guideActive && anordnungModus ? ABWEICHUNG_KURZ[anordnungModus] : undefined;
  const zusammenfassung = [
    buehne && `Bühne: ${BUEHNEN_MODUS_TEXT[buehne.modus].knopf}`,
    guideActive && anordnungModus && `Kanzler: ${ANORDNUNG_TITEL[anordnungModus]}`,
    `Darstellung: ${DARSTELLUNG[ui.mode]}`,
    onScreenChange && `Bildschirm ${screenId}`,
    fehlendeChats > 0 && `${fehlendeChats} offene Chat${fehlendeChats === 1 ? '' : 's'} noch nicht im Fenster`,
  ].filter(Boolean).join(' · ');

  const knopf = <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
    <span>Ansicht: <b style={{ fontWeight: 700 }}>{stand}</b>{kanzlerAbweichung && <span data-ai-id="cui-ansicht-kanzler-abweichung" style={{ color: 'var(--tn-warning, var(--tn-orange))' }}> · {kanzlerAbweichung}</span>}{onScreenChange && screenId === '2' ? ' · B2' : ''}</span>
    {fehlendeChats > 0 && <span className="cui-ansicht-zahl" aria-label={`${fehlendeChats} offene Chats nicht im Fenster`}>{fehlendeChats}</span>}
    <span aria-hidden="true">▾</span>
  </span>;

  return <KopfMenue aiId="cui-ansicht-menue" menueTitel="Ansicht" breite={720} knopf={knopf}
    knopfTitel={`Ansicht einstellen — ${zusammenfassung}`} klasse="cui-ansicht-knopf">
    {/* Eine Wahl, die die Flaeche umbaut, schliesst das Menue: am Tablet deckt es sonst die
        Buehne fast ganz zu, und man sieht nicht, dass sich etwas getan hat (03.10.2026). */}
    {(schliessen: () => void) => <div className="cui-ansicht-menue">
      {buehne && <Gruppe titel="Fenster der Bühne" aiId="cui-ansicht-buehne"
        satz="Wie die Fenster der offenen Bühne auf dem Bildschirm stehen.">
        <div className="cui-ansicht-wahlen">
          {(['dokument', 'tabs', 'buehne'] as BuehnenModus[]).map(m => {
            const kein = m === 'dokument' && !buehne.hatDokument;
            return <Wahl key={m} an={buehne.modus === m} aiId={`buehnen-modus-${m}`} disabled={kein}
              title={kein ? 'Diese Bühne hat keine eindeutige Seite, die man allein zeigen könnte.' : BUEHNEN_MODUS_TEXT[m].erklaerung}
              onClick={() => { buehne.waehlen(m); schliessen(); }}>
              <span className="cui-ansicht-wahl-name">{BUEHNEN_MODUS_TEXT[m].knopf}</span>
              <span className="cui-ansicht-wahl-satz">{kein ? 'Hier nicht möglich: keine eindeutige Seite.' : BUEHNEN_MODUS_TEXT[m].erklaerung}</span>
            </Wahl>;
          })}
        </div>
      </Gruppe>}

      {onAnordnung && <Gruppe titel="Guide und Kanzler" aiId="project-tabs-anordnung"
        satz={anordnungFestgelegt
          ? 'Mit zweitem Bildschirm steht der Kanzler fest rechts neben dem Guide.'
          : 'Wo Kanzler und Vizekanzler stehen. Standard: unter dem Guide, der Kanzler dieses Hauses links, der des anderen Hauses rechts. Eine Wahl öffnet den Guide.'}>
        <div className="cui-ansicht-wahlen cui-ansicht-wahlen-zwei">
          {ANORDNUNG_MODI.map(m => <Wahl key={m} an={guideActive && anordnungModus === m} aiId={`project-tabs-anordnung-${m}`}
            disabled={anordnungFestgelegt} onClick={() => { onAnordnung(m); schliessen(); }}>
            <span className="cui-ansicht-wahl-zeile"><AnordnungBild modus={m} /> <span className="cui-ansicht-wahl-name">{ANORDNUNG_TITEL[m]}</span></span>
          </Wahl>)}
        </div>
      </Gruppe>}

      {guideActive && <Gruppe titel="Spalte links" aiId="cui-ansicht-spalte"
        satz="Teile der Guide-Spalte ein- und ausklappen, um mehr Platz für die Bühne zu haben.">
        <div className="cui-ansicht-wahlen cui-ansicht-wahlen-zwei">
          {/* Zeigt den TATSAECHLICHEN Zustand (Rafael, 03.10.2026 11:25): bei „Nur Kanzler" ist der
              Guide aus, bei „Nur Guide" der Kanzler — auch wenn die Spalte selbst offen ist. Ein Tipp
              holt dann die Standardanordnung zurueck, statt nur den Einklapp-Merker umzuschalten. */}
          <Wahl an={guideTeilAn} aiId="guide-collapse"
            onClick={() => { if (guideTeilWegAnordnung && anordnungModus && !anordnungFestgelegt) onAnordnung?.('uebereinander'); else setUiPreferences({ guideCollapsed: !props.guideEingeklappt, focus: false }); schliessen(); }}>
            <span className="cui-ansicht-wahl-name">Guide {guideTeilWegAnordnung ? 'ist ausgeblendet' : props.guideEingeklappt ? 'ist eingeklappt' : 'ist offen'}</span>
            <span className="cui-ansicht-wahl-satz">{guideTeilWegAnordnung ? `„${ANORDNUNG_TITEL.nurChat}" gewählt. Tippen zeigt Guide und Kanzler.` : props.guideEingeklappt ? 'Tippen zum Öffnen.' : 'Tippen zum Einklappen.'}</span>
          </Wahl>
          <Wahl an={kanzlerTeilAn} aiId="guide-coach-collapse"
            onClick={() => { if (kanzlerTeilWegAnordnung && anordnungModus && !anordnungFestgelegt) onAnordnung?.('uebereinander'); else setUiPreferences({ coachCollapsed: !kanzlerZu, guideCollapsed: false, focus: false }); schliessen(); }}>
            <span className="cui-ansicht-wahl-name">Kanzler {kanzlerTeilWegAnordnung ? 'ist ausgeblendet' : kanzlerZu ? 'ist eingeklappt' : 'ist offen'}</span>
            <span className="cui-ansicht-wahl-satz">{kanzlerTeilWegAnordnung ? `„${ANORDNUNG_TITEL.nurGuide}" gewählt. Tippen zeigt Guide und Kanzler.` : kanzlerZu ? 'Tippen zum Öffnen.' : 'Tippen zum Einklappen.'}</span>
          </Wahl>
          <Wahl an={ui.focus} aiId="guide-focus" onClick={() => { setUiPreferences({ focus: !ui.focus }); schliessen(); }}>
            <span className="cui-ansicht-wahl-name">Fokus auf die Bühne</span>
            <span className="cui-ansicht-wahl-satz">{ui.focus ? 'An. Tippen stellt die vorige Aufteilung wieder her.' : 'Guide und Kanzler werden schmal, die Bühne breit.'}</span>
          </Wahl>
        </div>
      </Gruppe>}

      <Gruppe titel="Offene Chats anordnen" aiId="cui-ansicht-chats"
        satz="Holt alle offenen Chats dieses Arbeitsbereichs als Fenster in die Anordnung, damit keiner versteckt bleibt.">
        <button type="button" className="cui-ansicht-wahl" data-ai-id="project-tabs-auto-layout"
          disabled={props.layoutLaeuft} onClick={props.onLayout}
          style={fehlendeChats > 0 ? { borderColor: 'var(--tn-orange)', color: 'var(--tn-orange)' } : undefined}>
          <span className="cui-ansicht-wahl-name">{props.layoutLaeuft ? 'Wird angeordnet …'
            : fehlendeChats > 0 ? `Jetzt anordnen — ${fehlendeChats} Chat${fehlendeChats === 1 ? '' : 's'} fehlen` : 'Jetzt anordnen'}</span>
          {fehlendeChats === 0 && !props.layoutLaeuft && <span className="cui-ansicht-wahl-satz">Im Moment fehlt kein offener Chat.</span>}
        </button>
        {props.layoutMeldung && <span role="status" style={{ color: props.layoutFarbe }}>{props.layoutMeldung}</span>}
      </Gruppe>

      <Gruppe titel="Darstellung" aiId="cui-ansicht-darstellung"
        satz="Wie dicht die Oberfläche ist. Auto passt Abstände an den freien Platz an; Kompakt zeigt mehr auf einmal.">
        <div className="cui-ansicht-wahlen cui-ansicht-wahlen-drei">
          {(['auto', 'standard', 'compact'] as const).map(mode =>
            <Wahl key={mode} an={ui.mode === mode} aiId={`cui-view-${mode}`} onClick={() => setUiPreferences({ mode })}>
              <span className="cui-ansicht-wahl-name">{DARSTELLUNG[mode]}</span>
            </Wahl>)}
        </div>
      </Gruppe>

      <Gruppe titel="Größe der Oberfläche" aiId="cui-ansicht-groesse"
        satz="Alles größer oder kleiner zeigen. Kleinere Werte schaffen mehr Platz; die Wahl gilt nur auf diesem Gerät.">
        <DisplayScaleControl />
        <button type="button" className="cui-ansicht-wahl" data-ai-id="cui-ansicht-zuruecksetzen"
          onClick={() => setUiPreferences({ mode: 'auto', guideCollapsed: null, coachCollapsed: null, focus: false })}>
          <span className="cui-ansicht-wahl-name">Automatische Aufteilung zurücksetzen</span>
        </button>
      </Gruppe>

      {onScreenChange && <Gruppe titel="Bildschirm" aiId="cui-ansicht-bildschirm"
        satz="Ein zweites Browserfenster kann Bildschirm 2 sein — mit eigener Anordnung, die den ersten nie überschreibt.">
        <div className="cui-ansicht-wahlen cui-ansicht-wahlen-zwei">
          {(['1', '2'] as const).map(sid => <Wahl key={sid} an={screenId === sid} aiId={`project-tabs-screen-select-${sid}`}
            onClick={() => { onScreenChange(sid); schliessen(); }}>
            <span className="cui-ansicht-wahl-name">Bildschirm {sid}</span>
          </Wahl>)}
        </div>
      </Gruppe>}
    </div>}
  </KopfMenue>;
}
